import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { renderGenerator } from '../../utils/genArtRenderers/index';
import { renderStrataAsync } from '../../utils/genArtRenderers/strataWorkerManager';
import { renderMeridianAsync } from '../../utils/genArtRenderers/strataWorkerManager';
import { renderNormiePortraitAsync } from '../../utils/genArtRenderers/normiePortrait';
import {
  DEFAULT_GENART_CONFIG,
  LEGACY_TYPE_MAP,
  GENERATOR_MAP,
  getDefaultParams,
  PALETTE_MAP,
  preparePalette,
} from '../../constants/genArt';

/**
 * Phase B+D: Canvas2D generative art background.
 *
 * Replaces the old SVG feTurbulence pipeline with Canvas2D renderers.
 * Supports both old SVG config format (preset/base/accent) and new
 * Canvas2D format (type/paletteId/seed/hueShift/saturation/brightness/params).
 *
 * Phase D additions:
 * - Strata type renders via Web Worker (non-blocking) when available
 *
 * Renders at 1080×1350 (Instagram 4:5) and CSS-scales to fill container.
 * Debounces re-render on config changes to keep the UI responsive.
 *
 * @param {Object} props.config — generative config from slide data
 * @param {string} [props.prefix] — unused (kept for API compat with old component)
 */
export function GenerativeBackground({ config, prefix = 'gen' }) {
  const canvasRef = useRef(null);
  const renderTimeoutRef = useRef(null);
  const lastRenderedConfigRef = useRef(null);
  const asyncRenderIdRef = useRef(0); // track async render to ignore stale results

  // ── Normalize config (old SVG format → new Canvas2D format) ──
  const normalizedConfig = useMemo(() => normalizeConfig(config), [config]);

  // ── Stable render function ──
  const doRender = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const W = 1080;
    const H = 1350;

    // Set physical pixel size (only if changed)
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width = W;
      canvas.height = H;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Check if this is a strata or meridian type — use async worker
    if (normalizedConfig.type === 'strata' || normalizedConfig.type === 'meridian') {
      const renderId = ++asyncRenderIdRef.current;
      const { paletteId, seed, hueShift = 0, saturation = 100, brightness = 100, params = {} } = normalizedConfig;
      const rawPalette = PALETTE_MAP[paletteId];
      if (rawPalette) {
        const palette = preparePalette(rawPalette, hueShift, saturation / 100, brightness / 100);
        // Show a quick fill while worker processes
        ctx.fillStyle = palette.bg;
        ctx.fillRect(0, 0, W, H);
        const renderFn = normalizedConfig.type === 'meridian' ? renderMeridianAsync : renderStrataAsync;
        renderFn(ctx, W, H, palette, params, seed)
          .then(() => {
            // Only update if this is still the current render
            if (asyncRenderIdRef.current === renderId) {
              lastRenderedConfigRef.current = normalizedConfig;
            }
          })
          .catch(err => {
            if (asyncRenderIdRef.current === renderId) {
              console.warn('[GenerativeBackground] Async render failed:', err);
            }
          });
      }
      return;
    }

    // Check if this is a normie portrait type — use async image loader
    if (normalizedConfig.type === 'normiePortrait') {
      const renderId = ++asyncRenderIdRef.current;
      const { paletteId, seed, hueShift = 0, saturation = 100, brightness = 100, params = {} } = normalizedConfig;
      const rawPalette = PALETTE_MAP[paletteId];
      if (rawPalette) {
        const palette = preparePalette(rawPalette, hueShift, saturation / 100, brightness / 100);
        // Carry over the renderMode from the raw palette (not affected by color adjustments)
        palette.renderMode = rawPalette.renderMode;
        // Show bg fill while image loads
        ctx.fillStyle = palette.bg;
        ctx.fillRect(0, 0, W, H);
        renderNormiePortraitAsync(ctx, W, H, palette, params, seed)
          .then(() => {
            if (asyncRenderIdRef.current === renderId) {
              lastRenderedConfigRef.current = normalizedConfig;
            }
          })
          .catch(err => {
            if (asyncRenderIdRef.current === renderId) {
              console.warn('[GenerativeBackground] Normie portrait render failed:', err);
            }
          });
      }
      return;
    }

    try {
      renderGenerator(ctx, W, H, normalizedConfig);
    } catch (err) {
      console.error('[GenerativeBackground] Render error:', err);
      // Fallback: dark fill
      ctx.fillStyle = '#1a1a1a';
      ctx.fillRect(0, 0, W, H);
    }

    lastRenderedConfigRef.current = normalizedConfig;
  }, [normalizedConfig]);

  // ── Debounced render on config change ──
  useEffect(() => {
    // Clear any pending render
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // If this is the first render (no previous frame), render immediately
    if (!lastRenderedConfigRef.current) {
      doRender();
      return;
    }

    // Debounce subsequent renders (150ms)
    renderTimeoutRef.current = setTimeout(doRender, 150);

    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
    };
  }, [doRender]);

  return (
    <div className="absolute inset-0" data-generative-bg>
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ imageRendering: 'auto' }}
      />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  CONFIG NORMALIZATION (old SVG → new Canvas2D)
// ═══════════════════════════════════════════════════════════

/**
 * Convert any generative config to the new Canvas2D format.
 *
 * Handles:
 * 1. New format (has `type` field matching a known generator) → pass through
 * 2. Old SVG format (has `preset` field) → convert via LEGACY_TYPE_MAP
 * 3. Unknown/empty → use defaults
 */
function normalizeConfig(config) {
  if (!config) return DEFAULT_GENART_CONFIG;

  // ── New format: has 'type' that maps to a known generator ──
  if (config.type && GENERATOR_MAP[config.type]) {
    return {
      type: config.type,
      paletteId: config.paletteId || DEFAULT_GENART_CONFIG.paletteId,
      seed: config.seed ?? DEFAULT_GENART_CONFIG.seed,
      hueShift: config.hueShift ?? 0,
      saturation: config.saturation ?? 100,
      brightness: config.brightness ?? 100,
      params: config.params || getDefaultParams(config.type),
    };
  }

  // ── Old SVG format: has 'preset' field ──
  if (config.preset) {
    const mapping = LEGACY_TYPE_MAP[config.preset];
    if (mapping) {
      return {
        type: mapping.type,
        paletteId: mapping.paletteId,
        seed: config.seed ?? 42,
        hueShift: 0,
        saturation: 100,
        brightness: 100,
        params: getDefaultParams(mapping.type),
      };
    }
  }

  // ── Fallback to defaults ──
  return {
    type: DEFAULT_GENART_CONFIG.type,
    paletteId: DEFAULT_GENART_CONFIG.paletteId,
    seed: config.seed ?? DEFAULT_GENART_CONFIG.seed,
    hueShift: 0,
    saturation: 100,
    brightness: 100,
    params: getDefaultParams(DEFAULT_GENART_CONFIG.type),
  };
}
